import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  useTranslate, 
  useList, 
  ListContextProvider, 
  Datagrid, 
  TextField, 
  FunctionField,
  DateField,
  Pagination
} from 'react-admin';
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import BaseWidget from './base';
import { DashboardWidget, TableWidgetConfig, DataSourceItem } from '../../../types/dashboard.ui';
import { dashboardAPI } from '../../../api/dashboard';

interface TableWidgetProps {
  widget: DashboardWidget;
  editMode?: boolean;
  onEdit?: (widget: DashboardWidget) => void;
  onDelete?: (widget: DashboardWidget) => void;
}

interface TableData {
  id: string; // Required for React-Admin Datagrid
  timestamp: string;
  [key: string]: any;
}

const TableWidget: React.FC<TableWidgetProps> = ({ 
  widget,
  editMode,
  onEdit, 
  onDelete 
}: TableWidgetProps) => {
  const translate = useTranslate();
  const [data, setData] = useState<TableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const config = useMemo(() => 
    (typeof widget.config === 'string' ? JSON.parse(widget.config) : widget.config) as TableWidgetConfig || {}, 
    [widget.config]
  );

  // Parse and type the data source strictly
  const widgetDataSource = useMemo(() => {
    const source = typeof widget.data_source === 'string'
      ? JSON.parse(widget.data_source)
      : widget.data_source;
    return source as { items?: DataSourceItem[] } | null;
  }, [widget.data_source]);

  const dataSources = useMemo<DataSourceItem[]>(() => {
    // Priority: widget.data_source > config.dataSource
    if (widgetDataSource?.items && Array.isArray(widgetDataSource.items)) {
      return widgetDataSource.items;
    }
    if (config.dataSource?.items && Array.isArray(config.dataSource.items)) {
      return config.dataSource.items;
    }
    return [];
  }, [widgetDataSource, config.dataSource]);

  const loadTableData = useCallback(() => {
    if (!dataSources || dataSources.length === 0) {
      // Don't show error immediately, just empty state
      setLoading(false);
      return;
    }

    // Check for required parameters
    const firstSource = dataSources[0];
    if (!firstSource.deviceId || !firstSource.archiveTypeId) {
      setError(translate('resources.dashboards.widgets.table.no_data_source'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - 60 * 60 * 1000); // Last hour
    
    const deviceId = firstSource.deviceId;
    const archiveType = firstSource.archiveTypeId;
    const paramIds = dataSources.map(ds => ds.paramId);

    dashboardAPI.getHistoricalData(
      deviceId,
      archiveType,
      paramIds,
      startTime.toISOString(),
      endTime.toISOString()
    )
      .then((response) => {
        if (!response || response.length === 0) {
          setData([]);
          setLoading(false);
          return;
        }

        const paramMap = new Map(dataSources.map(ds => [ds.paramId, ds]));
        const timeSeriesMap = new Map<string, any>();

        response.forEach((series) => {
          const dataSource = paramMap.get(series.param_id);
          if (!dataSource || !series.values) return;

          const columnKey = dataSource.paramName || dataSource.paramVarname || series.param_id;

          series.values.forEach((point: any) => {
            const timestamp = new Date(point.time || point.timestamp).toISOString();
            
            if (!timeSeriesMap.has(timestamp)) {
              timeSeriesMap.set(timestamp, { 
                id: timestamp, // Use timestamp as ID
                timestamp 
              });
            }
            
            const row = timeSeriesMap.get(timestamp);
            row[columnKey] = point.value;
            
            if (dataSource.paramUnit) {
              row[`${columnKey}_unit`] = dataSource.paramUnit;
            }
          });
        });

        const tableData = Array.from(timeSeriesMap.values())
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()) as TableData[];

        setData(tableData);
      })
      .catch((err) => {
        console.error('Error loading table data:', err);
        setError(translate('resources.dashboards.widgets.table.error_loading'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataSources, translate]);

  useEffect(() => {
    loadTableData();
    
    const refreshInterval = config.refreshInterval || 30000;
    let interval: NodeJS.Timeout;
    if (refreshInterval > 0) {
      interval = setInterval(loadTableData, refreshInterval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loadTableData, config.refreshInterval]);

  // Prepare columns for Datagrid
  const columns = useMemo(() => {
    if (dataSources.length === 0) return [];
    
    return dataSources.map(ds => ({
      key: ds.paramName || ds.paramVarname || ds.paramId,
      title: translate(ds.paramLabel || ds.paramName || ds.paramVarname || ds.paramId, { _: ds.paramLabel || ds.paramName || ds.paramVarname || ds.paramId }),
      unit: ds.paramUnit
    }));
  }, [dataSources, translate]);

  // Use React-Admin's useList hook to manage list state
  const listContext = useList({
    data,
    isLoading: loading,
    perPage: 25,
    sort: { field: 'timestamp', order: 'DESC' },
    filter: {}
  });

  const content = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {loading && data.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : dataSources.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            {translate('resources.dashboards.widgets.table.no_data_source')}
          </Typography>
        </Box>
      ) : (
        <ListContextProvider value={listContext}>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Datagrid
              bulkActionButtons={false}
              sx={{
                '& .RaDatagrid-headerCell': {
                  fontWeight: 'bold',
                  backgroundColor: 'action.hover',
                }
              }}
            >
              {columns.map((col) => (
                <FunctionField
                  key={col.key}
                  label={col.title}
                  render={(record: any) => {
                    const value = record[col.key];
                    if (value === null || value === undefined) return '-';
                    const unit = record[`${col.key}_unit`] || col.unit || '';
                    return typeof value === 'number' 
                      ? `${value.toFixed(2)}${unit ? ' ' + unit : ''}`
                      : String(value);
                  }}
                />
              ))}
              <DateField 
                source="timestamp" 
                label={translate('resources.dashboards.widgets.table.columns.timestamp')} 
                showTime 
                options={{ 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit', 
                  hour: '2-digit', 
                  minute: '2-digit', 
                  second: '2-digit' 
                }}
              />
            </Datagrid>
          </Box>
          <Pagination />
        </ListContextProvider>
      )}
      
      {/* Removed total records count as Pagination handles it */}
    </Box>
  );

  return (
    <BaseWidget
      widget={widget}
      editMode={editMode}
      onEdit={onEdit ? () => onEdit(widget) : undefined}
      onDelete={onDelete ? () => onDelete(widget) : undefined}
    >
      {content}
    </BaseWidget>
  );
};

export default TableWidget;
